import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { api } from '../api/client.js';
import { useAuth } from '../auth/AuthProvider.js';
import { useI18n } from '../i18n/I18nProvider.js';
import { AppShell } from '../shell/AppShell.js';
import { MfaChallengeScreen } from '../screens/MfaChallengeScreen.js';
import {
  ForgotPasswordScreen,
  ResetPasswordScreen,
  VerifyEmailScreen,
} from '../screens/PasswordScreens.js';
import { AdminScreen } from '../screens/AdminScreen.js';
import { AutonomyScreen } from '../screens/AutonomyScreen.js';
import { FirstWorkspaceScreen } from '../screens/FirstWorkspaceScreen.js';
import { OnboardingScreen } from '../screens/OnboardingScreen.js';
import { DecisionsScreen } from '../screens/DecisionsScreen.js';
import { MailboxesScreen } from '../screens/MailboxesScreen.js';
import { MailScreen } from '../screens/MailScreen.js';
import { MoneyScreen } from '../screens/MoneyScreen.js';
import { SearchScreen } from '../screens/SearchScreen.js';
import { ActivityScreen } from '../screens/ActivityScreen.js';
import { ButlerScreen } from '../screens/ButlerScreen.js';
import { WaitingScreen } from '../screens/WaitingScreen.js';
import { RegisterScreen } from '../screens/RegisterScreen.js';
import { SecurityScreen } from '../screens/SecurityScreen.js';
import { SignInScreen } from '../screens/SignInScreen.js';
import { TodayScreen } from '../screens/TodayScreen.js';
import { RescueScreen } from '../screens/RescueScreen.js';
import { Alert } from '../ui/index.js';

/**
 * Routing.
 *
 * The guard below distinguishes three states, not two: signed in, signed out, and *unreachable*.
 * Treating a network failure as a sign-out would throw someone back to the login screen every
 * time their connection drops — and would tell them, wrongly, that something is wrong with their
 * account rather than with the network.
 */

const rootRoute = createRootRoute({ component: () => <Outlet /> });

function RequireAuth({ children }: { children: ReactNode }): ReactNode {
  const { status, workspaces, activeWorkspace } = useAuth();
  const { t } = useI18n();
  const onboarding = useQuery({
    queryKey: ['onboarding-guard', activeWorkspace?.id],
    queryFn: () => {
      if (activeWorkspace === null) throw new Error('No active workspace');
      return api.get('/api/v1/onboarding', undefined, { tenantId: activeWorkspace.id });
    },
    enabled: status === 'signed-in' && activeWorkspace !== null,
    staleTime: 10_000,
  });

  if (status === 'loading') {
    return (
      <div className="boot" role="status" aria-live="polite">
        {t('common.loading')}
      </div>
    );
  }

  if (status === 'unreachable') {
    return (
      <div className="boot">
        <Alert tone="critical" title={t('auth.unreachable.title')}>
          {t('auth.unreachable.body')}
        </Alert>
      </div>
    );
  }

  if (status === 'signed-out') {
    // A full navigation rather than a client-side redirect, so the session cookie state and the
    // rendered tree cannot disagree.
    window.location.assign('/sign-in');
    return null;
  }

  /*
   * Signed in, and belonging to nothing.
   *
   * Registration does not create a workspace — it cannot, because it answers identically whether or
   * not the address already has an account — so the first one is made here. Rendering the shell
   * instead would put somebody in a product where every screen answers "workspace not found".
   *
   * It is checked before the shell rather than as a route, so it also catches the person whose only
   * membership was revoked while they had the page open.
   */
  if (workspaces.length === 0) {
    return <FirstWorkspaceScreen />;
  }

  if (onboarding.isPending) {
    return (
      <div className="boot" role="status" aria-live="polite">
        {t('common.loading')}
      </div>
    );
  }

  if (onboarding.isError) {
    return (
      <div className="boot">
        <Alert tone="critical" title={t('onboarding.error.title')}>
          {t('onboarding.error.body')}
        </Alert>
      </div>
    );
  }

  const onboardingPath = window.location.pathname === '/welcome';
  const mailboxStepPath =
    window.location.pathname === '/settings/mailboxes' &&
    new URLSearchParams(window.location.search).get('onboarding') === '1';
  const rescueStepPath =
    window.location.pathname === '/rescue' &&
    new URLSearchParams(window.location.search).get('onboarding') === '1';
  if (
    onboarding.data.finishedAt === null &&
    !onboardingPath &&
    !mailboxStepPath &&
    !rescueStepPath
  ) {
    window.location.replace('/welcome');
    return null;
  }

  return <AppShell>{children}</AppShell>;
}

// Generic over the literal path so the router's type registration keeps each route's exact
// path, which is what makes `navigate({ to: '/mfa' })` a compile-time check.
const publicRoute = <Path extends string>(path: Path, component: () => ReactNode) =>
  createRoute({ getParentRoute: () => rootRoute, path, component });

const signInRoute = publicRoute('/sign-in', SignInScreen);
const registerRoute = publicRoute('/register', RegisterScreen);
const verifyEmailRoute = publicRoute('/verify-email', VerifyEmailScreen);
const forgotPasswordRoute = publicRoute('/forgot-password', ForgotPasswordScreen);
const resetPasswordRoute = publicRoute('/reset-password', ResetPasswordScreen);
const mfaRoute = publicRoute('/mfa', MfaChallengeScreen);

const todayRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: () => (
    <RequireAuth>
      <TodayScreen />
    </RequireAuth>
  ),
});

const mailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/mail',
  component: () => (
    <RequireAuth>
      <MailScreen />
    </RequireAuth>
  ),
});

const moneyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/money',
  component: () => (
    <RequireAuth>
      <MoneyScreen />
    </RequireAuth>
  ),
});

const searchRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/search',
  component: () => (
    <RequireAuth>
      <SearchScreen />
    </RequireAuth>
  ),
});

const activityRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/activity',
  component: () => (
    <RequireAuth>
      <ActivityScreen />
    </RequireAuth>
  ),
});

const butlerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/butler',
  component: () => (
    <RequireAuth>
      <ButlerScreen />
    </RequireAuth>
  ),
});

const waitingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/waiting',
  component: () => (
    <RequireAuth>
      <WaitingScreen />
    </RequireAuth>
  ),
});

const decisionsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/decisions',
  component: () => (
    <RequireAuth>
      <DecisionsScreen />
    </RequireAuth>
  ),
});

const securityRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/security',
  component: () => (
    <RequireAuth>
      <SecurityScreen />
    </RequireAuth>
  ),
});

const onboardingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/welcome',
  component: () => (
    <RequireAuth>
      <OnboardingScreen />
    </RequireAuth>
  ),
});

const autonomyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/autonomy',
  component: () => (
    <RequireAuth>
      <AutonomyScreen />
    </RequireAuth>
  ),
});

const mailboxesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/mailboxes',
  component: () => (
    <RequireAuth>
      <MailboxesScreen />
    </RequireAuth>
  ),
});

const rescueRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/rescue',
  component: () => (
    <RequireAuth>
      <RescueScreen />
    </RequireAuth>
  ),
});

const adminRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin',
  component: () => (
    <RequireAuth>
      <AdminScreen />
    </RequireAuth>
  ),
});

const settingsIndexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings',
  beforeLoad: () => {
    // TanStack Router signals a redirect by throwing its own sentinel, which is not an Error.
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw redirect({ to: '/settings/security' });
  },
});

const routeTree = rootRoute.addChildren([
  todayRoute,
  mailRoute,
  moneyRoute,
  searchRoute,
  activityRoute,
  butlerRoute,
  waitingRoute,
  decisionsRoute,
  signInRoute,
  registerRoute,
  verifyEmailRoute,
  forgotPasswordRoute,
  resetPasswordRoute,
  mfaRoute,
  settingsIndexRoute,
  securityRoute,
  autonomyRoute,
  mailboxesRoute,
  rescueRoute,
  adminRoute,
  onboardingRoute,
]);

export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  // Rendering nothing while a route resolves would flash an empty page; the shell's own loading
  // state is better than the router's.
  defaultPendingMs: 300,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
